import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import { useEffect, useState } from 'react'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import { Bebas_Neue, Space_Grotesk, Geist_Mono } from 'next/font/google'

import { SiteHeader } from '@/components/generals/site-header'

const display = Bebas_Neue({
    subsets: ['latin'],
    weight: '400'
})

const sans = Space_Grotesk({
    subsets: ['latin']
})

const mono = Geist_Mono({
    subsets: ['latin']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${display.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()
    const [leaving, setLeaving] = useState(false)

    /**
     * Raise the veil. Subscribed on the MODULE-LEVEL router, never on the
     * instance from useRouter(): that instance is a new object after every
     * navigation, so an effect keyed to it re-runs mid-transition and its
     * cleanup cancels the timer that lowers the veil again. That is how
     * the veil ended up covering the site permanently.
     */
    useEffect(() => {
        const start = (): void => setLeaving(true)

        Router.events.on('routeChangeStart', start)

        return () => {
            Router.events.off('routeChangeStart', start)
        }
    }, [])

    /* Lower it when the path that is actually rendered changes. This
       depends on the render, not on an event firing. */
    useEffect(() => {
        const timer = setTimeout(() => setLeaving(false), 200)

        return () => clearTimeout(timer)
    }, [router.asPath])

    /* And lower it regardless, so a navigation that stalls or is aborted
       can never leave the page trapped behind it. */
    useEffect(() => {
        if (!leaving) return

        const timer = setTimeout(() => setLeaving(false), 900)

        return () => clearTimeout(timer)
    }, [leaving])

    return (
        <div className='h-full font-sans'>
            <Head>
                <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
            </Head>

            <SiteHeader />

            <Component {...pageProps} />

            <div className='mo-route' data-active={leaving} aria-hidden />
        </div>
    )
}

export default App
